import { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import Select from 'react-select';
import { CSS } from '@dnd-kit/utilities';
import { IconChevronDown, IconGripVertical, IconPlus } from '@tabler/icons-react';
import { Controller } from 'react-hook-form';
import useTheme from 'chat-ui/src/hooks/useTheme';
import RequiredLabel from '@/components/labels/RequiredLabel';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { LangContext } from '@/context/LangProvider';
import { cn } from '@/lib/utils';
import probingQuestions from './components/probingQuestionsList';
import QuestionMediaUploading from './components/QuestionMediaUploading';
import useCreateProjectStore from '@/store/CreateProjectStore';
import SparklesIcon from '@/components/icons/SparklesIcon';
import getTextColor from '@/utils/getTextColorContrast';
import { QuestionBoxData } from '@/types';
import { Input } from '@/components/ui/input';
import CharacterCount from '@/components/projects/single-project/research-design/components/CharacterCount';
import { isEmbedProjectType } from '@/lib/projectTypeConfig';
import { QuestionCategories } from './QuestionCategories';

export function QuestionBox({
  id,
  register,
  registrationPath,
  displayNumber,
  control,
  handleDeleteQuestion,
  isActiveDrag,
  isUserViewer,
  existingText,
  isProjectPublished,
  isProjectCompleted,
  openQuestionId,
  setOpenQuestionId,
  setFileData,
  fileData,
  currentQuestion,
  singleProjectData,
  setValue,
  clearErrors,
  error,
  isQuestionSaved,
}: QuestionBoxData) {
  const { lang } = useContext(LangContext);
  const { setIsFormChanged, uploadingQuestionId } = useCreateProjectStore();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const isOpen = openQuestionId === id;
  const [toggleAiProbingFields, setToggleAiProbingFields] = useState(
    !!currentQuestion?.probing_goal,
  );
  const [characterCount, setCharacterCount] = useState(existingText?.length || 0);
  const { currentTheme } = useTheme();
  const questionText = currentQuestion?.question || existingText || '';

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    border: isDragging ? '2px dashed #718096' : '1px solid #e2e8f0',
    opacity: isDragging ? 0.7 : 1,
    boxShadow: isDragging ? '0 4px 6px rgba(0, 0, 0, 0.4)' : 'none',
  };

  useLayoutEffect(() => {
    if (isActiveDrag) {
      setOpenQuestionId(null);
    }
  }, [isActiveDrag, setOpenQuestionId]);

  useEffect(() => {
    if (!fileData[id]) {
      setFileData((prevFileData) => ({
        ...prevFileData,
        [id]: {
          id: '',
          file: undefined,
        },
      }));
    }
  }, [id, fileData, setFileData]);

  const hasProbingGoal = currentQuestion?.follow_up_question !== '0';
  const isDisabled =
    isUserViewer || isProjectPublished || isProjectCompleted || uploadingQuestionId === id;
  const hasHiddenError = error && !isOpen;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex flex-col w-full gap-5 bg-white border rounded-md shadow h-fit text-dark-text',
        hasHiddenError && 'border-red-500 border-2 validation-error',
      )}
    >
      <button
        type="button"
        className={cn(
          'flex items-center justify-between w-full p-6',
          hasHiddenError && 'bg-red-100',
        )}
        disabled={!!uploadingQuestionId}
        onClick={() => setOpenQuestionId(isOpen ? null : id)}
      >
        {/* question title */}
        <h3 className={cn('flex items-left w-[83%] font-medium', hasHiddenError && 'text-red-700')}>
          {displayNumber}
          {questionText && questionText.trim() !== '' && <span>:</span>}
          {questionText && questionText.trim() !== '' && (
            <span className="ml-1 text-left">{questionText}</span>
          )}
        </h3>
        <div className="flex items-center gap-4">
          {/* probing questions number */}
          <p className="mx-1 text-sm">{currentQuestion?.follow_up_question}</p>
          <span>
            <IconChevronDown
              size={20}
              className={`transition-all ease-in-out ${isOpen && 'rotate-180'}`}
            />
          </span>
          {/* drag item */}
          {!isUserViewer && !isProjectCompleted && !isProjectPublished && (
            <span className={`${!isOpen ? 'flex' : 'hidden'}`} {...attributes} {...listeners}>
              <IconGripVertical
                size={24}
                className="relative z-50 text-light-gray cursor-grabbing"
              />
            </span>
          )}
        </div>
      </button>
      <div className={cn('flex flex-col gap-5 p-6 pt-0', openQuestionId !== id && 'hidden')}>
        <RequiredLabel title="question" tooltip={lang.get('msg.questionInfo')}>
          <Textarea
            {...register(`${registrationPath}.question`)}
            onChange={(e) => {
              setCharacterCount(e.target.value.length);
              setIsFormChanged(true);
              setValue(`${registrationPath}.question`, e.target.value, {
                shouldDirty: true,
              });
              if (error && e.target.value.trim() !== '') {
                clearErrors(`${registrationPath}.question`);
              }
            }}
            maxLength={700}
            disabled={isDisabled}
            placeholder={lang.get('msg.questionPlaceholder')}
            className={cn('h-24', error && 'border-red-500')}
          />
          <CharacterCount
            maxCharacters={700}
            currentCharacters={characterCount}
            errorMessage={error?.message}
          />
        </RequiredLabel>
        {/* uploading media */}
        {currentQuestion && (
          <QuestionMediaUploading
            registrationPath={registrationPath}
            isProjectPublished={isProjectPublished}
            isProjectCompleted={isProjectCompleted}
            setValue={setValue}
            currentQuestion={currentQuestion}
            isQuestionSaved={isQuestionSaved}
          />
        )}

        <div className="flex flex-col gap-4">
          <div className="flex w-full flex-col gap-2">
            <div className="flex w-full gap-8 md700:flex-col">
              <div className="flex flex-col w-full gap-6 md700:w-full">
                {/* follow up question */}
                <RequiredLabel
                  icon={
                    <SparklesIcon
                      className="p-1 rounded-md size-6"
                      style={{
                        backgroundColor: currentTheme?.primary,
                        color: getTextColor(currentTheme?.primary || ''),
                      }}
                    />
                  }
                  title="numberOfAiProbingQuestions"
                  tooltip={lang.get('msg.probingQuestionInfo')}
                >
                  <Controller
                    name={`${registrationPath}.follow_up_question`}
                    control={control}
                    render={({ field }) => (
                      <div className="relative w-full">
                        <Select
                          value={probingQuestions.find(
                            (option) => option.value === currentQuestion?.follow_up_question,
                          )}
                          onChange={(value) => {
                            field.onChange(value?.value);
                            setIsFormChanged(true);
                            setValue(`${registrationPath}.follow_up_question`, value?.value, {
                              shouldDirty: true,
                            });
                          }}
                          options={probingQuestions}
                          isDisabled={isDisabled}
                          placeholder={field?.value}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isFocused ? currentTheme?.primary : '#FFFFFF',
                              color: state.isFocused
                                ? getTextColor(currentTheme?.primary as string)
                                : '#000000',
                              ':active': {
                                backgroundColor: currentTheme?.primary,
                              },
                            }),
                            control: (styles) => ({
                              ...styles,
                              backgroundColor: isDisabled
                                ? currentTheme?.['input-invalid-color']
                                : '#FFFFFF',
                              borderColor: currentTheme?.['input-invalid-color'],
                            }),
                          }}
                        />
                      </div>
                    )}
                  />
                </RequiredLabel>
              </div>
            </div>
            {hasProbingGoal && (
              <>
                {!toggleAiProbingFields && !currentQuestion?.probing_goal && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="flex w-auto ml-auto mt-2 items-center cursor-pointer px-3.5 py-2.5"
                    onClick={() => setToggleAiProbingFields(!toggleAiProbingFields)}
                  >
                    <div className="text-sm flex items-center gap-2">
                      <IconPlus className="size-5" />
                      {lang.get('msg.addProbingGuide')}
                    </div>
                  </Button>
                )}
                <div className="flex flex-col gap-2">
                  {/* probing goal */}
                  {hasProbingGoal && toggleAiProbingFields && (
                    <div className="flex flex-col mt-2">
                      <RequiredLabel
                        notRequired
                        title="probingGuide"
                        tooltip={lang.get('msg.probingGuideInfo')}
                      >
                        <Controller
                          name={`${registrationPath}.probing_goal`}
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder={lang.get('msg.probingGuidePlaceholder')}
                              onChange={(e) => {
                                field.onChange(e.target.value);
                                setIsFormChanged(true);
                                setValue(`${registrationPath}.probing_goal`, e.target.value, {
                                  shouldDirty: true,
                                });
                              }}
                              disabled={isDisabled}
                            />
                          )}
                        />
                      </RequiredLabel>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* #5 — category authoring is an EMBED feature (drives CODING_READY to
            the host). Gate it on project_type so it does not leak into the
            regular interview editor (which also fired a GET /categories per
            question). */}
        {isEmbedProjectType(singleProjectData?.project_type) &&
          !isProjectPublished &&
          !isProjectCompleted &&
          singleProjectData?._id && (
            <QuestionCategories
              projectId={singleProjectData._id}
              questionId={id}
              questionText={questionText}
              isDisabled={isDisabled}
              isQuestionSaved={isQuestionSaved}
            />
          )}

        <Button
          disabled={isDisabled}
          variant="destructive"
          type="button"
          onClick={() => handleDeleteQuestion(id)}
          className="self-end"
        >
          {lang.get('msg.deleteQuestion')}
        </Button>
      </div>
    </div>
  );
}
